import {
	type FxIdDomainSettingsPublicWebClientConfigOptionsLegalContactsValue,
	FxIdDomainSettingsUrlOpenMethod,
	FxIdWebFeaturesGamesChooseGamePortalConfig,
	FxGamesLibWebPublicDataBase
} from "../Api/gen";
import i18n from "../i18n";

export function getValueByPath<T>(obj: any, path: string): T | undefined {
	return path.split(".").reduce((acc, part) => acc && acc[part], obj) as T;
}

// Helper function to find adapter configuration
export function findAdapterConfig<T extends { IframeType?: string }>(
	adapters: T[] | undefined | null,
	adapter?: string
): T | undefined {
	if (!adapter || !adapters) {
		return undefined;
	}
	return adapters.find((el) => el.IframeType === adapter);
}

export function getTurnstileSiteKey(
	portalConfig: FxIdWebFeaturesGamesChooseGamePortalConfig,
	gameConfig?: FxGamesLibWebPublicDataBase
): string | undefined {
	if (process.env.NODE_ENV === "development") {
		return "3x00000000000000000000FF";
	}

	return gameConfig?.Turnstile.SiteKey;
}

export function getValueFromConfigWithOverrides<T>(
	portalConfig: FxIdWebFeaturesGamesChooseGamePortalConfig,
	portalConfigCompanyBrandingPath: string,
	gameConfigPath: string,
	adapter?: string,
	gameConfig?: FxGamesLibWebPublicDataBase
): T | undefined {
	let result: T | undefined;

	// Step 1: Extract value from portalConfig.PortalPublicWebConfig.CompanyBranding using portalConfigCompanyBrandingPath
	const portalBrandingValue = getValueByPath<T>(
		portalConfig.PortalPublicWebConfig.CompanyBranding,
		portalConfigCompanyBrandingPath
	);
	if (portalBrandingValue) result = portalBrandingValue;

	// Step 2: Check portalConfig.PortalPublicWebConfig.CompanyBranding.Adapters using custom key (IframeType)
	const portalAdapterOverride = findAdapterConfig(
		portalConfig.PortalPublicWebConfig.CompanyBranding.Adapters,
		adapter
	);
	if (portalAdapterOverride) {
		const adapterValue = getValueByPath<T>(portalAdapterOverride, gameConfigPath);
		if (adapterValue) result = adapterValue;
	}

	// Step 3: Check gameConfig.PublicWebClientConfig for the value using gameConfigPath if gameConfig is provided
	if (gameConfig) {
		const gamePublicConfigValue = getValueByPath<T>(gameConfig.PublicWebClientConfig, gameConfigPath);
		if (gamePublicConfigValue) result = gamePublicConfigValue;

		// Step 4: Check gameConfig.PublicWebClientConfig.Adapters using custom key (IframeType) if gameConfig is provided
		const gameAdapterOverride = findAdapterConfig(gameConfig.PublicWebClientConfig.Adapters, adapter);
		if (gameAdapterOverride) {
			const adapterValue = getValueByPath<T>(gameAdapterOverride, gameConfigPath);
			if (adapterValue) result = adapterValue;
		}
	}

	return result;
}

export function getDocsAndLegal(
	portalConfig: FxIdWebFeaturesGamesChooseGamePortalConfig,
	gameConfig?: FxGamesLibWebPublicDataBase,
	adapter?: string
): {
	tos?: {
		url: string;
		openMethod: FxIdDomainSettingsUrlOpenMethod;
	};
	pp?: {
		url: string;
		openMethod: FxIdDomainSettingsUrlOpenMethod;
	};
	legalContacts?: {
		[key: string]: FxIdDomainSettingsPublicWebClientConfigOptionsLegalContactsValue;
	};
	localeLegalContacts?: FxIdDomainSettingsPublicWebClientConfigOptionsLegalContactsValue;
} {
	const legalContacts = getValueFromConfigWithOverrides<{
		[key: string]: FxIdDomainSettingsPublicWebClientConfigOptionsLegalContactsValue;
	}>(portalConfig, "__no_such_path__", "LegalContacts", adapter, gameConfig);

	const locale = i18n.resolvedLanguage;
	const localeLegalContacts = legalContacts == null || locale == null ? undefined : legalContacts[locale];

	const currentPpUrl =
		getValueFromConfigWithOverrides<string>(portalConfig, "ExternalPpUrl", "ExternalPpUrl", adapter, gameConfig) ??
		gameConfig?.PpUrl;

	const currentPpUrlMethod =
		getValueFromConfigWithOverrides<FxIdDomainSettingsUrlOpenMethod>(
			portalConfig,
			"PpUrlOpenMethod",
			"PpUrlOpenMethod",
			adapter,
			gameConfig
		) ?? gameConfig?.PpUrlOpenMethod;

	let pp:
		| {
				url: string;
				openMethod: FxIdDomainSettingsUrlOpenMethod;
		  }
		| undefined = undefined;

	if (currentPpUrl != null) {
		pp = {
			url: currentPpUrl,
			openMethod: currentPpUrlMethod ?? FxIdDomainSettingsUrlOpenMethod.Unspecified
		};
	}

	const currentTosUrl =
		getValueFromConfigWithOverrides<string>(
			portalConfig,
			"ExternalTosUrl",
			"ExternalTosUrl",
			adapter,
			gameConfig
		) ?? gameConfig?.TosUrl;

	const currentTosUrlOpenMethod =
		getValueFromConfigWithOverrides<FxIdDomainSettingsUrlOpenMethod>(
			portalConfig,
			"TosUrlOpenMethod",
			"TosUrlOpenMethod",
			adapter,
			gameConfig
		) ?? gameConfig?.TosUrlOpenMethod;

	let tos:
		| {
				url: string;
				openMethod: FxIdDomainSettingsUrlOpenMethod;
		  }
		| undefined = undefined;

	if (currentTosUrl != null) {
		tos = {
			url: currentTosUrl,
			openMethod: currentTosUrlOpenMethod ?? FxIdDomainSettingsUrlOpenMethod.Unspecified
		};
	}

	return { tos, pp, legalContacts, localeLegalContacts };
}
